import { type ApolloError, useApolloClient } from '@apollo/client';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import uniqBy from 'lodash-es/uniqBy';
import {
  type Dispatch,
  type ForwardedRef,
  forwardRef,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  EmployeeListSortField,
  type ListEmployeesSortInput,
  ListMinimalEmployeesDocument,
  type ListMinimalEmployeesQuery,
  type ListMinimalEmployeesQueryVariables,
  useListMinimalEmployeesLazyQuery,
} from '@/common/graphql';
import { ApiUtils, toIdArray } from '@/common/utils';

import type { EmployeeSearchResultRow } from '../../employee-search';
import { EmployeeSearchPopup } from '../../employee-search';
import { DEFAULT_PAGE_SIZE } from '../../table';
import type { CommonProps } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';
import type { Data, Props, Variables } from './types';

/**
 * Fallback sort order applied whenever a consumer omits its own `sort` prop.
 * Shared by every EmployeeSelectSingle/EmployeeSelectMultiple call site that
 * doesn't pass `sort` explicitly (Manager selection, Job History, Offboarding
 * reassignment, Employee List filters, Probation Review filters) - changing
 * this changes default sort behavior for all of them at once.
 */
const defaultSortOrder: ListEmployeesSortInput[] = [
  { field: EmployeeListSortField.Level, desc: true },
  { field: EmployeeListSortField.HiredDate, desc: false },
  { field: EmployeeListSortField.PreferredFullName, desc: false },
];

const toComboboxData = (row: EmployeeSearchResultRow): Data => ({
  value: row.id,
  label: row.generalInformation.preferredFullName,
  disabled: false,
  generalInformation: row.generalInformation,
  currentMainContract: row.currentMainContract,
});

const toMinimalEmployeeData = (it: ListMinimalEmployeesQuery['listEmployees']['items'][number]): Data =>
  ({
    ...it,
    value: it.id,
    label: it.generalInformation.preferredFullName,
    labelFormatted: (
      <div className="flex items-center justify-between gap-1">
        <span className="flex-1">{it.generalInformation.preferredFullName}</span>
        <span className="text-gray-7 text-3xs">{it.currentMainContract?.company?.countryId}</span>
      </div>
    ),
    disabled: false,
  }) as Data;

/**
 * Strips fields from the combobox's own dropdown `variables` before handing them to
 * `EmployeeSearchPopup` as `baseVariables`, for two distinct reasons:
 *
 * - `includeIds`/`excludeIds` scope the combobox's own paginated dropdown (e.g. excluding the
 *   current employee, including the currently-selected id so it survives pagination) - they
 *   aren't meant to restrict the popup's separate "browse everyone" query. Every current caller
 *   also sets a non-empty `statuses`, so passing them through wouldn't itself trigger the
 *   backend's NoCondition-collapse bug today (see EmployeeSearchForm.tsx's isolated-seed-query
 *   comment) - but a future `includeIds`-only caller could silently reproduce it. Stripping them
 *   here removes that risk regardless of what future callers pass.
 * - `companyIds`/`countryIds` have a dedicated soft-default channel (`defaultCompanyId`/
 *   `defaultCountryId` - pre-check the popup's Company/Country filter without restricting it).
 *   Letting them reach `baseVariables` would additionally hard-lock those two filters via
 *   `createAllowlistFilter` (`EmployeeSearchFilters.tsx`), defeating the whole point of the soft
 *   default: a caller that scopes its own inline dropdown to a sensible default company/country
 *   (e.g. the target employee's own) still wants the popup to let the user pick ANY company/
 *   country, not just that default. `departmentIds` is deliberately NOT stripped here - unlike
 *   company/country it has no soft-default counterpart, and its hard lock (FR-017) is intentional.
 */
const toPopupBaseVariables = (variables: Variables | undefined): NonNullable<Variables> => {
  const {
    includeIds: _includeIds,
    excludeIds: _excludeIds,
    companyIds: _companyIds,
    countryIds: _countryIds,
    ...rest
  } = variables ?? {};
  return rest;
};

/**
 * Merges the `excludeIds` prop (hard-excluded from both the inline dropdown and the popup, e.g.
 * FR-022's Target Employee/sibling-field exclusion) into `variables.excludeIds` (which today only
 * scopes the inline dropdown, per `toPopupBaseVariables` above) for the inline dropdown's own
 * query. The popup receives the `excludeIds` prop separately - see its `baseVariables` below.
 */
const mergeExcludeIds = (variables: Variables | undefined, excludeIds: string | string[] | undefined): Variables => ({
  ...variables,
  excludeIds: [...new Set([...(variables?.excludeIds ?? []), ...toIdArray(excludeIds)])],
});

/**
 * Fetches full row data for `ids` via a one-off imperative `client.query`, fully isolated from the
 * main paginated `filter` in `useQuery` and from the `load` lazy-query hook instance. Previously
 * these ids were merged into `filter.includeIds` directly - since `filter` is reused by every later
 * query (`handleQueryNext`, keyword search), that permanently polluted it, and per this feature's
 * Addendum 3 investigation, collapses the result set to ONLY those ids whenever `filter`'s other
 * fields are empty (jOOQ's `NoCondition` is absorbed by `.or()` regardless of side, so
 * `noCondition().or(id IN (...))` renders as `id IN (...)` alone - not "everyone, plus these"). A
 * second `useLazyQuery` hook instance for the same query document was tried next, but a plain
 * `client.query` one-off call avoids any possibility of the two hook instances' underlying
 * observables interfering with each other.
 *
 * Never throws: on a GraphQL/network error, or when some requested id isn't returned (e.g. it no
 * longer exists server-side), that id comes back in `failedIds` rather than being silently
 * dropped - callers use this to stop treating it as still-loading.
 */
const fetchEmployeesByIds = async (
  client: ReturnType<typeof useApolloClient>,
  ids: string[],
): Promise<{ resolved: Data[]; failedIds: string[] }> => {
  try {
    const result = await client.query<ListMinimalEmployeesQuery, ListMinimalEmployeesQueryVariables>({
      query: ListMinimalEmployeesDocument,
      fetchPolicy: 'cache-first',
      variables: { pageNumber: 1, pageSize: ids.length, filter: { includeIds: ids }, sort: [] },
    });
    const items = result.data?.listEmployees?.items ?? [];
    const returnedIds = new Set(items.map(it => it.id));
    return { resolved: items.map(toMinimalEmployeeData), failedIds: ids.filter(id => !returnedIds.has(id)) };
  } catch (error) {
    ApiUtils.handleApiError(error as ApolloError);
    return { resolved: [], failedIds: ids };
  }
};

/**
 * Resolve externally-set `value` id(s) that aren't in the currently-loaded `data` (e.g. editing an
 * employee whose current Manager isn't on the combobox's first page) via `fetchEmployeesByIds`.
 *
 * `failedIds` tracks ids that were fetched but never resolved into `data` so `isValueLoading`
 * doesn't stay `true` forever for them - without it, a single failed lookup would permanently
 * disable the whole combobox (shared across 6+ screens) with no recovery path short of a reload.
 */
const useResolveMissingEmployeeIds = ({
  value,
  data,
  initialLoading,
  setData,
}: {
  value: string | string[] | null | undefined;
  data: Array<Data>;
  initialLoading: boolean;
  setData: Dispatch<SetStateAction<Array<Data>>>;
}) => {
  const client = useApolloClient();
  const [failedIds, setFailedIds] = useState<Set<string>>(new Set());
  const missingIds = toIdArray(value).filter(id => !data.some(it => it.value === id));
  const idsToFetch = missingIds.filter(id => !failedIds.has(id));
  const idsToFetchKey = idsToFetch.join(',');

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fetch only when the set of ids actually needing a fetch changes (idsToFetchKey) or initialLoading/client change, not on every data/array identity change
  useEffect(() => {
    if (initialLoading || idsToFetch.length === 0) return;
    let cancelled = false;

    fetchEmployeesByIds(client, idsToFetch).then(({ resolved, failedIds: newlyFailed }) => {
      if (cancelled) return;
      if (resolved.length > 0) setData(prev => uniqBy([...prev, ...resolved], 'value'));
      if (newlyFailed.length > 0) setFailedIds(prev => new Set([...prev, ...newlyFailed]));
    });

    return () => {
      cancelled = true;
    };
  }, [idsToFetchKey, initialLoading, client]);

  return { isValueLoading: missingIds.some(id => !failedIds.has(id)) };
};

const useQuery = ({
  sort,
  variables,
  value,
  onLoadingCompleted,
}: CommonProps<Variables> & Props & { value: string | string[] | null | undefined }) => {
  const [filter, setFilter] = useState(variables);
  const [data, setData] = useState<Array<Data>>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [preventQueryNext, setPreventQueryNext] = useState(false);

  const pageNumber = useRef(1);
  const [load, { data: mutationResult }] = useListMinimalEmployeesLazyQuery({
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const items = mutationResult?.listEmployees?.items;
    if (items === undefined) return;

    const newData = items.map(toMinimalEmployeeData);

    setData(prev => uniqBy([...prev, ...newData], 'value'));
    setInitialLoading(false);
    setPreventQueryNext(newData.length < DEFAULT_PAGE_SIZE);
    onLoadingCompleted?.();
  }, [mutationResult?.listEmployees?.items, onLoadingCompleted]);

  const { isValueLoading } = useResolveMissingEmployeeIds({ value, data, initialLoading, setData });

  // Re-sync `filter` (and reset pagination/accumulated data) whenever the `variables` prop
  // meaningfully changes - `filter` is otherwise seeded once at mount and never updated, so a
  // caller whose `variables` fields are derived asynchronously (e.g. countryIds/departmentIds
  // resolved from a GraphQL query after mount, starting empty) would stay stuck on that initial
  // snapshot forever. Deep-value compare (not reference) since callers typically pass a fresh
  // object literal on every render. Preserves the user's in-progress `keyword` search text.
  const variablesRef = useRef(variables);
  useEffect(() => {
    if (isEqual(variablesRef.current, variables)) return;
    variablesRef.current = variables;
    pageNumber.current = 1;
    setData([]);
    setInitialLoading(true);
    setFilter(prev => ({ ...variables, keyword: prev?.keyword }));
  }, [variables]);

  const handleQueryNext = useCallback(async () => {
    if (preventQueryNext) {
      return;
    }

    pageNumber.current += 1;
    await load({
      variables: {
        pageNumber: pageNumber.current,
        pageSize: DEFAULT_PAGE_SIZE,
        filter: filter!,
        sort: sort ?? [],
      },
    });
  }, [filter, load, preventQueryNext, sort]);

  const resetPageNumber = useCallback(() => {
    pageNumber.current = 1;
  }, []);

  useEffect(() => {
    load({
      variables: {
        pageNumber: pageNumber.current,
        pageSize: DEFAULT_PAGE_SIZE,
        filter: filter!,
        sort: sort ?? [],
      },
    }).then();
  }, [filter, load, sort]);

  return { data, initialLoading, isValueLoading, preventQueryNext, handleQueryNext, setFilter, resetPageNumber };
};

// `leftSection` is Omitted from the public props because this component always injects its own
// EmployeeSearchPopup trigger there; a caller-supplied `leftSection` would be silently clobbered
// by the hardcoded `leftSection={...}` in the JSX below. Omitting makes that a compile-time error.
export const EmployeeSelectSingle = forwardRef(
  (props: Props & Omit<SelectSingleProps<Data>, 'leftSection'>, ref: ForwardedRef<HTMLDivElement>) => {
    const sort = useMemo(() => props.sort || defaultSortOrder, [props.sort]);
    const variables = useMemo(
      () => mergeExcludeIds(props.variables, props.excludeIds),
      [props.variables, props.excludeIds],
    );
    const { data, initialLoading, isValueLoading, preventQueryNext, handleQueryNext, setFilter, resetPageNumber } =
      useQuery({ ...props, variables, sort });

    const onConfirmPopup = useCallback(
      (selected: EmployeeSearchResultRow | EmployeeSearchResultRow[]) => {
        const row = Array.isArray(selected) ? selected[0] : selected;
        if (row) props.onChange?.(toComboboxData(row));
      },
      [props.onChange],
    );

    return (
      <SelectSingle
        ref={ref}
        dropdownMinWidth={400}
        {...props}
        data={data}
        leftSection={
          <EmployeeSearchPopup
            baseVariables={{ ...toPopupBaseVariables(props.variables), excludeIds: toIdArray(props.excludeIds) }}
            defaultCompanyId={props.defaultCompanyId}
            defaultCountryId={props.defaultCountryId}
            defaultDepartmentName={props.defaultDepartmentName}
            disabled={props.disabled || initialLoading || isValueLoading}
            mode="single"
            searchFilters={props.searchFilters}
            sort={sort}
            value={props.value}
            onConfirm={onConfirmPopup}
          />
        }
        loading={initialLoading || isValueLoading}
        showLoadMore={!preventQueryNext}
        onClickLoadMore={handleQueryNext}
        onSearchChange={keyword => {
          if (isEmpty(keyword.trim())) {
            resetPageNumber();
          }
          // `SelectMultiple`/`SelectSingle` reset their internal search text to '' every time
          // the dropdown closes (including closes triggered by focus moving to/from the
          // EmployeeSearchPopup's own modal in `leftSection`), which re-fires this callback via
          // `useSelect`'s debounced effect even when the keyword hasn't actually changed. Bail
          // out (return the same `prev` reference) in that case so React skips the re-render -
          // otherwise every one of those redundant calls creates a new `filter` object, which
          // re-triggers the main list query below indefinitely.
          setFilter(prev => (prev?.keyword === keyword ? prev : { ...prev, keyword }));
        }}
      />
    );
  },
);

// See EmployeeSelectSingle above: `leftSection` is Omitted because this component injects its own.
export const EmployeeSelectMultiple = (props: Props & Omit<SelectMultipleProps<Data>, 'leftSection'>) => {
  const sort = useMemo(() => props.sort || defaultSortOrder, [props.sort]);
  const variables = useMemo(
    () => mergeExcludeIds(props.variables, props.excludeIds),
    [props.variables, props.excludeIds],
  );
  const { data, initialLoading, isValueLoading, preventQueryNext, handleQueryNext, setFilter, resetPageNumber } =
    useQuery({
      ...props,
      variables,
      sort,
    });

  const onConfirmPopup = useCallback(
    (selected: EmployeeSearchResultRow | EmployeeSearchResultRow[]) => {
      const rows = Array.isArray(selected) ? selected : [selected];
      props.onChange?.(rows.map(toComboboxData));
    },
    [props.onChange],
  );

  return (
    <SelectMultiple
      dropdownMinWidth={400}
      multipleSelectedLabel={length => `${length} employees`}
      {...props}
      data={data}
      leftSection={
        <EmployeeSearchPopup
          baseVariables={{ ...toPopupBaseVariables(props.variables), excludeIds: toIdArray(props.excludeIds) }}
          defaultCompanyId={props.defaultCompanyId}
          defaultCountryId={props.defaultCountryId}
          defaultDepartmentName={props.defaultDepartmentName}
          disabled={props.disabled || initialLoading || isValueLoading}
          mode="multi"
          searchFilters={props.searchFilters}
          sort={sort}
          value={props.value}
          onConfirm={onConfirmPopup}
        />
      }
      loading={initialLoading || isValueLoading}
      showLoadMore={!preventQueryNext}
      onClickLoadMore={handleQueryNext}
      onSearchChange={keyword => {
        if (isEmpty(keyword.trim())) {
          resetPageNumber();
        }
        // See the matching comment in EmployeeSelectSingle above: bail out when the keyword
        // hasn't actually changed, so a redundant call (e.g. `SelectMultiple` resetting its
        // search text to '' on every dropdown close) doesn't create a new `filter` object and
        // re-trigger the main list query below indefinitely.
        setFilter(prev => (prev?.keyword === keyword ? prev : { ...prev, keyword }));
      }}
    />
  );
};
